/**
 * PremiumHeader — Bandeau navy/or O'SCOP réutilisable.
 *
 * Standardise tous les bandeaux d'écran sur l'identité O'SCOP Premium :
 *   - Fond navy `#0a3d62` avec grain or filigrane SVG inline
 *   - Halos blur or (top-right + bottom-left)
 *   - Logo OscopLogo officiel (anneau or + étoile 8-branches)
 *   - Breadcrumb tracking `O'SCOP Outremer · {subtitle}`
 *   - Titre Inter bold + sous-titre épuré
 *   - Slot `actions` pour boutons CTA (or institutionnel recommandé)
 *
 * Variants :
 *   - `default` : grand bandeau 6-8 padding (pleins écrans / pages dashboard)
 *   - `compact` : bandeau plus mince (panneaux internes)
 *
 * Usage :
 *   <PremiumHeader
 *     eyebrow="Centre de gestion"
 *     title="Tableau de Bord Administration"
 *     subtitle="Pilotage unifié de la plateforme."
 *     actions={<Button>...</Button>}
 *   />
 */
import React from 'react';
import OscopLogo from './OscopLogo';

const GRAIN_PATTERN = "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='80' height='80' viewBox='0 0 80 80'%3E%3Cg fill='%23DEA900' fill-opacity='0.5'%3E%3Ccircle cx='1' cy='1' r='1'/%3E%3C/g%3E%3C/svg%3E\")";

const COMPACT_GRAIN = "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40'%3E%3Ccircle cx='1' cy='1' r='1' fill='%23DEA900'/%3E%3C/svg%3E\")";

export default function PremiumHeader({
  eyebrow = 'Centre de gestion',
  title,
  subtitle,
  actions,
  variant = 'default',
  className = '',
  brand = "O'SCOP Outremer",
  testId,
  logoSize,
}) {
  const isCompact = variant === 'compact';
  const padding = isCompact ? 'p-5' : 'p-6 md:p-8';
  const shadow = isCompact
    ? 'shadow-md'
    : 'shadow-[0_20px_60px_-15px_rgba(10,61,98,0.45)]';
  const titleSize = isCompact
    ? 'text-lg md:text-xl'
    : 'text-2xl md:text-3xl';
  const computedLogoSize = logoSize ?? (isCompact ? 38 : 48);

  return (
    <div
      className={`group/header relative overflow-hidden rounded-2xl ${shadow} ${className}`}
      data-testid={testId}
    >
      {/* Fond navy O'SCOP */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#0a3d62] via-[#0c4a78] to-[#08304d]" />
      {/* Grain or filigrane */}
      <div
        className="absolute inset-0 opacity-[0.08] mix-blend-overlay"
        style={{ backgroundImage: isCompact ? COMPACT_GRAIN : GRAIN_PATTERN }}
      />
      {/* Halos or */}
      {!isCompact && (
        <>
          <div className="absolute -right-20 -top-20 w-72 h-72 rounded-full bg-[#DEA900]/15 blur-3xl" />
          <div className="absolute -left-32 -bottom-24 w-80 h-80 rounded-full bg-[#DEA900]/10 blur-3xl" />
        </>
      )}
      {isCompact && (
        <div className="absolute -right-12 -top-12 w-40 h-40 rounded-full bg-[#DEA900]/12 blur-2xl" />
      )}

      <div className={`relative ${padding}`}>
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
          <div className="flex items-center gap-4">
            {/* Logo O'SCOP officiel — anneau or + étoile 8-branches DOM-TOM */}
            <div className="shrink-0">
              <OscopLogo size={computedLogoSize} variant="on-navy" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] font-bold tracking-[0.18em] text-[#f5cc3e] uppercase">
                  {brand}
                </span>
                {eyebrow && (
                  <>
                    <span className="h-px w-8 bg-[#f5cc3e]/40" />
                    <span className="text-[10px] tracking-wider text-white/60 uppercase">
                      {eyebrow}
                    </span>
                  </>
                )}
              </div>
              <h2 className={`${titleSize} font-bold text-white tracking-tight mt-0.5`}>
                {title}
              </h2>
              {subtitle && (
                <p className="text-sm text-white/70 mt-0.5">{subtitle}</p>
              )}
            </div>
          </div>
          {actions && (
            <div className="flex flex-wrap gap-2 lg:justify-end">
              {actions}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
